import api from './api';

export interface DocumentGroup {
  _id: string;
  name: string;
  code: string;
  entityType: string;
  description?: string;
  zoneId?: string | { _id: string; name: string; code?: string };
  zoneIds?: Array<string | { _id: string; name: string; code?: string }>;
  serviceHub?: string;
  serviceHubs?: string[];
  serviceId?: string;
  isActive: boolean;
  isSystem?: boolean;
  sortOrder?: number;
}

export interface DocumentConfigItem {
  _id: string;
  name: string;
  code: string;
  entityType: string;
  groupId?: string | { _id: string; name?: string; code?: string };
  zoneId?: string | { _id: string; name: string };
  serviceHub?: string;
  serviceId?: string;
  isMandatory: boolean;
  identifierRequired?: boolean;
  verificationRequired: boolean;
  expiryRequired: boolean;
  isActive?: boolean;
  isSystem?: boolean;
  sortOrder?: number;
  allowedFormats: string[];
  maxSizeMB: number;
}

export interface UserDocumentItem {
  _id: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  fileUrl: string;
  documentNumber?: string;
  rejectionReason?: string;
  createdAt?: string;
  userId?: { _id?: string; firstName: string; lastName?: string; email?: string; phone?: string; userType: string; zoneId?: { _id: string; name: string; code?: string } };
  documentConfigId?: string | {
    _id: string;
    name: string;
    code: string;
    entityType: string;
    isMandatory: boolean;
    sortOrder?: number;
  };
}

export interface PartnerOnboardingRecord {
  _id: string;
  entityType: string;
  formCode?: string;
  status: string;
  rejectionReason?: string;
  userId?: {
    firstName: string;
    lastName?: string;
    email?: string;
    userType: string;
    isActive: boolean;
    zoneId?: { _id: string; name: string; code?: string } | string;
    serviceHub?: string;
  };
}

export interface OnboardingStatusResponse {
  approvalRequired: boolean;
  record?: PartnerOnboardingRecord;
  group?: DocumentGroup;
  documents: DocumentConfigItem[];
  compliance: {
    missing: DocumentConfigItem[];
    pending: UserDocumentItem[];
    rejected: UserDocumentItem[];
    approved: UserDocumentItem[];
    userDocs: UserDocumentItem[];
    allMandatoryApproved: boolean;
    allMandatoryUploaded: boolean;
    complete: boolean;
  };
  canComplete: boolean;
}

export async function getOnboardingRequirements(formCode: string) {
  const res = await api.get('/partner/onboarding/requirements', { params: { formCode } });
  return res.data.data as {
    group?: DocumentGroup;
    documents: DocumentConfigItem[];
    approvalRequired: boolean;
  };
}

export async function getOnboardingStatus(formCode: string) {
  const res = await api.get('/partner/onboarding/status', { params: { formCode } });
  return res.data.data as OnboardingStatusResponse;
}

export async function submitPartnerOnboarding(payload: {
  formCode: string;
  formData?: Record<string, unknown>;
}) {
  const res = await api.post('/partner/onboarding/submit', payload);
  return res.data.data;
}

export async function uploadPartnerDocument(
  file: File,
  documentConfigId: string,
  extras?: { documentNumber?: string; expiryDate?: string }
) {
  const form = new FormData();
  form.append('file', file);
  form.append('documentConfigId', documentConfigId);
  if (extras?.documentNumber) form.append('documentNumber', extras.documentNumber);
  if (extras?.expiryDate) form.append('expiryDate', extras.expiryDate);
  const res = await api.post('/partner/documents/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data.data;
}

export async function listDocumentGroups(params?: {
  entityType?: string;
  zoneId?: string;
  serviceHub?: string;
  includeInactive?: boolean;
}) {
  const res = await api.get('/admin/documents/groups', {
    params: {
      ...(params?.entityType ? { entityType: params.entityType } : {}),
      ...(params?.zoneId ? { zoneId: params.zoneId } : {}),
      ...(params?.serviceHub ? { serviceHub: params.serviceHub } : {}),
      ...(params?.includeInactive ? { includeInactive: 'true' } : {}),
    },
  });
  return res.data.data as DocumentGroup[];
}

function normalizeDocumentConfig(row: Record<string, unknown>): DocumentConfigItem {
  const raw = row as unknown as DocumentConfigItem;
  const groupId = raw.groupId;
  return {
    ...raw,
    groupId: typeof groupId === 'object' && groupId && '_id' in groupId ? groupId._id : groupId,
  };
}

export async function createDocumentGroup(body: Partial<DocumentGroup>) {
  const res = await api.post('/admin/documents/groups', body);
  return res.data.data as DocumentGroup;
}

export async function updateDocumentGroup(id: string, body: Partial<DocumentGroup>) {
  const res = await api.put(`/admin/documents/groups/${id}`, body);
  return res.data.data as DocumentGroup;
}

export async function listDocumentConfigs(params?: {
  entityType?: string;
  zoneId?: string;
  serviceHub?: string;
  includeInactive?: boolean;
}) {
  const res = await api.get('/admin/documents/config', {
    params: {
      ...(params?.entityType ? { entityType: params.entityType } : {}),
      ...(params?.zoneId ? { zoneId: params.zoneId } : {}),
      ...(params?.serviceHub ? { serviceHub: params.serviceHub } : {}),
      ...(params?.includeInactive ? { activeOnly: 'false' } : {}),
    },
  });
  return (res.data.data as Record<string, unknown>[]).map(normalizeDocumentConfig);
}

export async function createDocumentConfig(body: Partial<DocumentConfigItem>) {
  const res = await api.post('/admin/documents/config', body);
  return res.data.data as DocumentConfigItem;
}

export async function updateDocumentConfig(id: string, body: Partial<DocumentConfigItem>) {
  const res = await api.put(`/admin/documents/config/${id}`, body);
  return res.data.data as DocumentConfigItem;
}

export async function deleteDocumentGroup(id: string) {
  const res = await api.delete(`/admin/documents/groups/${id}`);
  return res.data.data;
}

export async function deleteDocumentConfig(id: string) {
  const res = await api.delete(`/admin/documents/config/${id}`);
  return res.data.data;
}

export async function listUserDocumentsQueue(
  status?: string,
  params?: { entityType?: string; zoneId?: string; serviceHub?: string },
) {
  const res = await api.get('/admin/documents/user-documents', {
    params: {
      ...(status ? { status } : {}),
      ...(params?.entityType ? { entityType: params.entityType } : {}),
      ...(params?.zoneId ? { zoneId: params.zoneId } : {}),
      ...(params?.serviceHub ? { serviceHub: params.serviceHub } : {}),
    },
  });
  return res.data.data as UserDocumentItem[];
}

export async function verifyUserDocument(id: string, status: 'approved' | 'rejected', rejectionReason?: string) {
  const res = await api.put(`/admin/documents/${id}/verify`, { status, rejectionReason });
  return res.data.data;
}

export interface PartnerDocumentReviewItem {
  partnerId: string;
  firstName: string;
  lastName?: string;
  email?: string;
  phone?: string;
  entityType: string;
  zoneId?: { _id: string; name: string } | string;
  serviceHub?: string;
  pendingDocCount: number;
  pendingDocuments?: Array<{ _id: string; name: string; uploadedAt?: string; sortOrder?: number }>;
  onboarding?: {
    _id: string;
    status: string;
    formCode?: string;
    submittedAt?: string;
    profileChangesPending?: boolean;
  };
}

export async function listPartnerDocumentReview(params?: {
  entityType?: string;
  zoneId?: string;
  serviceHub?: string;
}) {
  const res = await api.get('/admin/documents/partner-review', { params: params || {} });
  return res.data.data as PartnerDocumentReviewItem[];
}

export async function listPartnerOnboardings(params?: {
  status?: string;
  entityType?: string;
  zoneId?: string;
  serviceHub?: string;
}) {
  const res = await api.get('/admin/partner-onboarding', { params: params || {} });
  return res.data.data as PartnerOnboardingRecord[];
}

export async function approvePartnerOnboarding(id: string) {
  const res = await api.put(`/admin/partner-onboarding/${id}/approve`);
  return res.data.data;
}

export async function rejectPartnerOnboarding(id: string, reason: string) {
  const res = await api.put(`/admin/partner-onboarding/${id}/reject`, { reason });
  return res.data.data;
}
